import { ReactNode, useState } from 'react';
import { LuListTodo } from 'react-icons/lu';
import { Link } from 'react-router-dom';

import { NoteView } from '@ndvr/customer-api';

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { PlusDropdownMenuTrigger } from '@/components/ui/dropdown-menu-triggers';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/utils/cn';
import { PersonDrawer } from 'modules';
import { personRoute } from 'utils';
import { EventDetailsDrawerByGuid } from 'views/household/HouseholdEvents/EventDetailsDrawer';
import { CreateOpportunitySheet } from 'views/opportunities/widgets/CreateOpportunitySheet';
import { toSuggestedOpportunityFormValues } from 'views/opportunities/widgets/OpportunityForm';
import { OpportunityViewDetailsSheet } from 'views/opportunities/widgets/OpportunityViewDetailsSheet';
import { toSuggestedPersonFormValues } from 'views/person/person-form-utils';
import { useListTasksByNoteQuery } from 'views/tasks/task-queries';
import { CreateTaskSheet } from 'views/tasks/widgets/CreateTaskSheet';
import { toActionItemFormValues } from 'views/tasks/widgets/TaskForm';
import { TaskViewDetailsSheet } from 'views/tasks/widgets/TaskViewDetailsSheet';

import { MEETING_MINUTES_LINK_CLASSES } from '../meeting-minutes-list-utils';
import { useDismissNoteSuggestion, useNoteSuggestions } from '../note-suggestion-queries';
import { CreatedHubItem, NoteSuggestion, NoteSuggestionType, toCreatedHubItems } from '../note-suggestion-utils';
import { NewEventSheet, toSuggestedMeetingFormValues } from './NewEventSheet';

type SuggestionKind<T extends NoteSuggestionType> = {
    label: string;
    classes: string;
    // A row's action: create the entity while pending, open it once created.
    renderPending: (suggestion: Extract<NoteSuggestion, { type: T }>, noteGUID: string) => ReactNode;
    renderCreated: (createdGUID: string) => ReactNode;
};

const SUGGESTION_CTA_CLASSES =
    'border-cocoa-900 bg-cocoa-900 text-cocoa-0 shrink-0 rounded-[3px] border px-[9px] py-1 text-[10px] font-medium tracking-[0.06em] uppercase';

// Sheets are mounted only while open, so a controlled sheet's onOpenChange(false) maps to onClose.
const whenClosed = (onClose: () => void) => (open: boolean) => {
    if (!open) onClose();
};

const SUGGESTION_KINDS: { [T in NoteSuggestionType]: SuggestionKind<T> } = {
    TASK: {
        label: 'Task',
        classes: 'bg-cocoa-900 text-cocoa-0',
        renderPending: ({ actionItem }, noteGUID) => (
            <SheetButton
                label="Create task"
                renderSheet={(onClose) => (
                    <CreateTaskSheet
                        householdGUID={actionItem.householdGUID}
                        defaultFormValues={toActionItemFormValues(actionItem)}
                        actionItemGUID={actionItem.actionItemGUID}
                        noteGUID={noteGUID}
                        onClose={onClose}
                    />
                )}
            />
        ),
        renderCreated: (createdGUID) => (
            <SheetButton
                label="Open task"
                renderSheet={(onClose) => (
                    <TaskViewDetailsSheet open taskGuid={createdGUID} onOpenChange={whenClosed(onClose)} />
                )}
            />
        ),
    },
    OPPORTUNITY: {
        label: 'Opportunity',
        classes: 'bg-seafoam-300 text-cocoa-900',
        renderPending: ({ suggestedOpportunity }, noteGUID) => (
            <SheetButton
                label="Create opportunity"
                renderSheet={(onClose) => (
                    <CreateOpportunitySheet
                        householdGUID={suggestedOpportunity.householdGUID}
                        defaultFormValues={toSuggestedOpportunityFormValues(suggestedOpportunity)}
                        link={{ suggestedOpportunityGUID: suggestedOpportunity.suggestedOpportunityGUID, noteGUID }}
                        onClose={onClose}
                    />
                )}
            />
        ),
        renderCreated: (createdGUID) => (
            <SheetButton
                label="Open opportunity"
                renderSheet={(onClose) => (
                    <OpportunityViewDetailsSheet
                        open
                        opportunityGuid={createdGUID}
                        onOpenChange={whenClosed(onClose)}
                    />
                )}
            />
        ),
    },
    PERSON: {
        label: 'Person',
        classes: 'bg-seafoam-100 text-cocoa-900',
        renderPending: ({ suggestedPerson }, noteGUID) => (
            <SheetButton
                label="Add person"
                renderSheet={(onClose) => (
                    <PersonDrawer
                        mode="create-household"
                        globalAccountGUID={suggestedPerson.householdGUID}
                        defaultValues={toSuggestedPersonFormValues(suggestedPerson)}
                        link={{ suggestedPersonGUID: suggestedPerson.suggestedPersonGUID, noteGUID }}
                        onClose={onClose}
                    />
                )}
            />
        ),
        // People have no view sheet, so this is a real link to the person page.
        renderCreated: (createdGUID) => (
            <Link to={personRoute(createdGUID)} className={SUGGESTION_CTA_CLASSES}>
                Open person
            </Link>
        ),
    },
    MEETING: {
        label: 'Meeting',
        classes: 'bg-seafoam-500 text-cocoa-0',
        renderPending: ({ suggestedMeeting }, noteGUID) => (
            <SheetButton
                label="Schedule meeting"
                renderSheet={(onClose) => (
                    <NewEventSheet
                        open
                        onOpenChange={whenClosed(onClose)}
                        defaultHousehold={{ value: suggestedMeeting.householdGUID, label: '' }}
                        disableHouseholdSelection
                        defaultValues={toSuggestedMeetingFormValues(suggestedMeeting)}
                        link={{ suggestedMeetingGUID: suggestedMeeting.suggestedMeetingGUID, noteGUID }}
                    />
                )}
            />
        ),
        renderCreated: (createdGUID) => (
            <SheetButton
                label="Open meeting"
                renderSheet={(onClose) => (
                    <EventDetailsDrawerByGuid eventGUID={createdGUID} isOpen onOpenChange={whenClosed(onClose)} />
                )}
            />
        ),
    },
};

const SUGGESTIONS_CARD_CLASSES = 'bg-cocoa-0 flex min-w-0 flex-col gap-2 rounded-md p-3.5';
const SUGGESTION_ROW_CLASSES = 'border-cocoa-100 flex flex-col gap-[5px] border-b pb-2';

// isProcessing: the meeting transcript is still being processed, so the suggestions are not
// extracted yet and the card shows its loading skeleton instead of "Nothing left to review."
// householdGUID: the household that Add task files under, from the page's route.
export const NewMeetingMinutesSuggestions = ({
    note,
    householdGUID,
    isProcessing,
}: {
    note: NoteView;
    householdGUID: string;
    isProcessing?: boolean;
}) => {
    const suggestionsQuery = useNoteSuggestions(note.noteGUID);
    const tasksQuery = useListTasksByNoteQuery(note.noteGUID);
    const isLoading = suggestionsQuery.isLoading || tasksQuery.isLoading || isProcessing;
    const isError = suggestionsQuery.isError;
    // The created list merges both sources, so a failure in either makes it incomplete: it shows an
    // error instead of a partial list and a count that reads as complete.
    const isCreatedError = isError || tasksQuery.isError;
    const created = toCreatedHubItems(suggestionsQuery.data, tasksQuery.data ?? []);
    const pending = suggestionsQuery.data.filter((suggestion) => !suggestion.createdGUID);
    const pendingLabel = pending.length === 1 ? '1 suggestion' : `${pending.length} suggestions`;
    const [isAddTaskOpen, setIsAddTaskOpen] = useState(false);

    return (
        <div className={SUGGESTIONS_CARD_CLASSES}>
            {/* Waits for every source like the skeleton does, so the card never grows a section mid-load. */}
            {!isLoading && (
                <>
                    <SectionHeader
                        aside={
                            <span className="flex items-center gap-1.5">
                                {!isCreatedError && (created.length === 1 ? '1 item' : `${created.length} items`)}
                                {/* Only tasks can be listed back by note today; other types join once the
                                    backend can list them (plans/2026-10-07-ad-1549-created-hub-items.md §2). */}
                                <DropdownMenu>
                                    <PlusDropdownMenuTrigger ariaLabel="Add hub item" />
                                    <DropdownMenuContent align="end">
                                        <DropdownMenuItem onClick={() => setIsAddTaskOpen(true)}>
                                            <LuListTodo className="mr-2 h-4 w-4" />
                                            Add task
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </span>
                        }
                    >
                        Created hub items
                    </SectionHeader>
                    {isCreatedError ? (
                        <p className="text-cocoa-500 py-1.5 text-[11px]">Unable to load created items</p>
                    ) : (
                        created.length > 0 && (
                            <div className="flex flex-col gap-2">
                                {created.map((item) => (
                                    <CreatedHubItemRow key={item.key} item={item} />
                                ))}
                            </div>
                        )
                    )}
                    <Separator className="bg-cocoa-300" />
                </>
            )}
            <SectionHeader aside={isLoading || isError ? undefined : pendingLabel}>Claire&trade; found</SectionHeader>
            {isLoading ? (
                <SuggestionsSkeleton />
            ) : isError ? (
                <p className="text-cocoa-500 py-1.5 text-[11px]">Unable to load suggestions</p>
            ) : pending.length === 0 ? (
                <p className="text-cocoa-500 py-1.5 text-[11px]">Nothing left to review.</p>
            ) : (
                pending.map((suggestion) => (
                    <PendingSuggestionRow
                        key={suggestion.suggestionGUID}
                        suggestion={suggestion}
                        noteGUID={note.noteGUID}
                    />
                ))
            )}
            {isAddTaskOpen && (
                <CreateTaskSheet
                    householdGUID={householdGUID}
                    defaultFormValues={{}}
                    noteGUID={note.noteGUID}
                    onClose={() => setIsAddTaskOpen(false)}
                />
            )}
        </div>
    );
};

// aside: a count, or a link action, on the right of the label.
const SectionHeader = ({ children, aside }: { children: ReactNode; aside?: ReactNode }) => (
    <div className="flex items-baseline justify-between">
        <span className="text-cocoa-500 text-[10px] font-medium tracking-[0.16em] uppercase">{children}</span>
        {aside && <span className="text-cocoa-500 text-[10px]">{aside}</span>}
    </div>
);

const PendingSuggestionRow = ({ suggestion, noteGUID }: { suggestion: NoteSuggestion; noteGUID: string }) => {
    const kind = SUGGESTION_KINDS[suggestion.type];
    // TS cannot correlate suggestion.type with the matching registry entry, so widen the renderer
    // to the full union here; each entry was type-checked against its own kind above.
    const renderPending = kind.renderPending as SuggestionKind<NoteSuggestionType>['renderPending'];
    const dismiss = useDismissNoteSuggestion(noteGUID);
    // Optimistic: the row disappears on click and only comes back if the dismiss request fails.
    if (dismiss.isPending) return null;
    return (
        <SuggestionRowLayout suggestion={suggestion}>
            <button
                type="button"
                className={cn(MEETING_MINUTES_LINK_CLASSES, 'shrink-0')}
                onClick={() => dismiss.mutate(suggestion)}
            >
                Dismiss
            </button>
            {renderPending(suggestion, noteGUID)}
        </SuggestionRowLayout>
    );
};

// Created rows always sit in their own section container, so the last one can drop its border
// above the divider.
const CreatedHubItemRow = ({ item }: { item: CreatedHubItem }) => (
    <SuggestionRowLayout suggestion={item} className="last:border-b-0 last:pb-0">
        {SUGGESTION_KINDS[item.type].renderCreated(item.createdGUID)}
    </SuggestionRowLayout>
);

const SUGGESTION_CHIP_CLASSES =
    'shrink-0 rounded-[3px] px-1.5 py-[3px] text-[8px] font-medium tracking-[0.1em] uppercase';

// Type chip and title, then meta with the row's action on the right.
const SuggestionRowLayout = ({
    suggestion,
    className,
    children,
}: {
    suggestion: Pick<NoteSuggestion, 'type' | 'title' | 'meta'>;
    className?: string;
    children: ReactNode;
}) => {
    const kind = SUGGESTION_KINDS[suggestion.type];
    return (
        <div className={cn(SUGGESTION_ROW_CLASSES, className)}>
            <div className="flex items-baseline gap-2">
                <span className={cn(SUGGESTION_CHIP_CLASSES, kind.classes)}>{kind.label}</span>
                <span className="text-xs leading-[1.45]">{suggestion.title}</span>
            </div>
            <div className="flex items-center gap-2.5">
                <span className="text-cocoa-500 min-w-0 flex-1 truncate text-[11px]">{suggestion.meta}</span>
                {children}
            </div>
        </div>
    );
};

// The row CTA; its sheet is mounted only while open and closing it hands control back here.
const SheetButton = ({ label, renderSheet }: { label: string; renderSheet: (onClose: () => void) => ReactNode }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <>
            <button type="button" onClick={() => setIsOpen(true)} className={SUGGESTION_CTA_CLASSES}>
                {label}
            </button>
            {isOpen && renderSheet(() => setIsOpen(false))}
        </>
    );
};

// Also the note page's loading placeholder for the rail cards, so the rows don't change shape on load.
export const SuggestionsSkeleton = () => (
    <>
        {[0, 1, 2].map((row) => (
            <div key={row} className={SUGGESTION_ROW_CLASSES}>
                <div className="flex items-center gap-2">
                    <div className="h-3.5 w-12 shrink-0 animate-pulse rounded-[3px] bg-gray-200" />
                    <div className="h-3 flex-1 animate-pulse rounded bg-gray-200" />
                </div>
                <div className="flex items-center gap-2.5">
                    <div className="h-2.5 w-1/3 flex-1 animate-pulse rounded bg-gray-200" />
                    <div className="h-6 w-20 shrink-0 animate-pulse rounded-[3px] bg-gray-200" />
                </div>
            </div>
        ))}
    </>
);
